import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNetworkStatus } from '@/shared/stores/network-status.store'
import { Toast } from '@/shared/ui/organisms/toast'
import { toast as rtToast } from 'react-toastify'

const OFFLINE_TOAST_ID = 'network-offline-toast'

/**
 * Global network-status side-effect provider:
 * - Listens to window `online` / `offline` events and syncs to Zustand store.
 * - Shows a persistent warning toast while offline.
 * - Shows a success toast + invalidates all queries when the user reconnects.
 *
 * Renders nothing — must be mounted inside QueryClientProvider and ToastProvider.
 */
export function NetworkProvider({ children }: { children: React.ReactNode }) {
  const { isOnline, wasOffline, setOnline, setOffline, clearWasOffline } =
    useNetworkStatus()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const hasMountedRef = useRef(false)

  // Subscribe to browser online/offline events once
  useEffect(() => {
    const onOnline = () => setOnline()
    const onOffline = () => setOffline()
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [setOnline, setOffline])

  // Show persistent offline toast
  useEffect(() => {
    if (!isOnline) {
      // react-toastify: persistent toast (no auto-close) via direct API
      // Our Toast wrapper forces autoClose via `duration || 3000`, so we call
      // the underlying API directly for the indefinite "you are offline" state.
      if (!rtToast.isActive(OFFLINE_TOAST_ID)) {
        rtToast.warn(t('you_are_offline'), {
          toastId: OFFLINE_TOAST_ID,
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          position: 'top-right',
          theme: 'colored',
        })
      }
      hasMountedRef.current = true
    } else {
      // Dismiss offline toast once we're back online
      if (rtToast.isActive(OFFLINE_TOAST_ID)) {
        rtToast.dismiss(OFFLINE_TOAST_ID)
      }
    }
  }, [isOnline, t])

  // Handle online reconnect: success toast + global refetch
  useEffect(() => {
    if (!hasMountedRef.current) {
      // Don't show "restored" on initial mount when online
      hasMountedRef.current = true
      return
    }
    if (isOnline && wasOffline) {
      Toast({
        type: 'success',
        message: t('internet_restored'),
        duration: 3000,
      })
      // Invalidate everything — triggers a refetch of all active queries
      queryClient.invalidateQueries()
      clearWasOffline()
    }
  }, [isOnline, wasOffline, queryClient, clearWasOffline, t])

  return <>{children}</>
}

export default NetworkProvider
